import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Chain, Checklist, CtaLink, Heading, Section, requestLink } from './Blocks';
import posterImage from '../../assets/talentech-poster.jpg';
import ServicesOverview from './ServicesOverview';

const list = (t, key) => t(key, { returnObjects: true });

export function Traction() {
  const { t } = useTranslation();
  const cities = list(t, 'landing.traction.cities');
  return (
    <Section tone="white">
      <Heading kicker={t('landing.traction.kicker')} title={t('landing.traction.title')} />
      <div className="grid stats traction">
        <div className="stat"><div className="n">100+</div><div className="l">{t('landing.traction.pros')}</div></div>
        <div className="stat"><div className="n">60+</div><div className="l">{t('landing.traction.missions')}</div></div>
        <div className="stat"><div className="n">RDC</div><div className="l">{t('landing.traction.rdc')}</div></div>
      </div>
      <p className="cities">{cities.join(' • ')}</p>
      <p className="lead-line">{t('landing.traction.ambition')}</p>
    </Section>
  );
}

export function Problem() {
  const { t } = useTranslation();
  const cols = list(t, 'landing.problem.cols');
  return (
    <Section>
      <Heading title={t('landing.problem.title')} />
      <p className="lead-line">{t('landing.problem.lead')}</p>
      <p className="muted" style={{ marginBottom: '1.5rem' }}>{t('landing.problem.body')}</p>
      <div className="grid cols3">
        {cols.map((c) => (
          <div key={c.who} className="card pad">
            <i className={`bi bi-${c.icon} big-icon`} />
            <span className="badge blue">{c.who}</span>
            <h3 style={{ margin: '.6rem 0 .8rem' }}>{c.q}</h3>
            <ul className="bullets">{c.items.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        ))}
      </div>
      <p className="bridge">{t('landing.problem.bridge')}</p>
      <Chain items={list(t, 'landing.problem.bridgeFlow')} className="center-chain" />
    </Section>
  );
}

export function Solution() {
  const { t } = useTranslation();
  return (
    <Section tone="white">
      <Heading title={t('landing.solution.title')} />
      <p className="lead-line">{t('landing.solution.lead')}</p>
      <p className="muted" style={{ marginBottom: '1rem' }}>{t('landing.solution.body')}</p>
      <Chain items={list(t, 'landing.solution.chain')} />
      <p className="lead-line" style={{ marginTop: '1.5rem' }}>{t('landing.solution.idea')}</p>
    </Section>
  );
}

export function HowItWorks() {
  const { t } = useTranslation();
  const steps = list(t, 'landing.how.steps');
  return (
    <Section>
      <Heading title={t('landing.how.title')} sub={t('landing.how.sub')} />
      <div className="how-grid">
        <div className="stack">
          {steps.map((s, i) => (
            <div key={s.t} className="card pad step">
              <span className="step-n">{String(i + 1).padStart(2, '0')}</span>
              <strong>{s.t}</strong>
              <span className="muted">{s.d}</span>
            </div>
          ))}
        </div>
        <figure className="poster">
          <img src={posterImage} alt={t('landing.how.imageAlt')} width="1121" height="1403" loading="lazy" />
        </figure>
      </div>
      <p className="kicker" style={{ marginTop: '1.75rem' }}>{t('landing.how.systemLabel')}</p>
      <Chain items={list(t, 'landing.how.system')} />
      <p className="lead-line" style={{ marginTop: '1rem' }}>{t('landing.how.key')}</p>
    </Section>
  );
}

const PILLAR_LINKS = ['/technicians', requestLink('workforce'), requestLink('academy')];

export function Solutions() {
  const { t } = useTranslation();
  const cards = list(t, 'landing.solutions.cards');
  return (
    <>
      <Section id="services" tone="white">
        <Heading title={t('landing.solutions.title')} sub={t('landing.solutions.sub')} />
        <p className="muted" style={{ marginBottom: '1.5rem' }}>{t('landing.solutions.lead')}</p>
        <div className="grid cols3">
          {cards.map((c, i) => (
            <div key={c.name} className="card pad pillar">
              <i className={`bi bi-${c.icon} big-icon`} />
              <h3>{c.name}</h3>
              <strong>{c.q}</strong>
              <p className="muted">{c.d}</p>
              {c.note && <p className="pillar-note">{c.note}</p>}
              <CtaLink to={PILLAR_LINKS[i]}>{c.cta}</CtaLink>
            </div>
          ))}
        </div>
      </Section>
      <ServicesOverview />
    </>
  );
}

export function Journey() {
  const { t } = useTranslation();
  return (
    <Section tone="white">
      <Heading title={t('landing.journey.title')} sub={t('landing.journey.sub')} />
      <p className="muted" style={{ marginBottom: '1rem' }}>{t('landing.journey.body')}</p>
      <Chain items={list(t, 'landing.journey.chain')} />
      <p className="lead-line" style={{ marginTop: '1.5rem' }}>{t('landing.journey.vision')}</p>
    </Section>
  );
}

export function Inclusion() {
  const { t } = useTranslation();
  return (
    <Section>
      <Heading title={t('landing.inclusion.title')} sub={t('landing.inclusion.sub')} />
      <p className="muted" style={{ marginBottom: '1.5rem' }}>{t('landing.inclusion.lead')}</p>
      <div className="grid cols3">
        {list(t, 'landing.inclusion.cards').map((c) => (
          <div key={c.t} className="card pad">
            <i className={`bi bi-${c.icon} big-icon`} />
            <strong>{c.t}</strong>
            <p className="muted">{c.d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
}

export function Impact() {
  const { t } = useTranslation();
  return (
    <Section tone="white">
      <Heading title={t('landing.impact.title')} sub={t('landing.impact.sub')} />
      <p className="muted" style={{ marginBottom: '1.5rem' }}>{t('landing.impact.lead')}</p>
      <div className="grid cols4">
        {list(t, 'landing.impact.groups').map((g) => (
          <div key={g.t} className="card pad">
            <i className={`bi bi-${g.icon} big-icon`} />
            <strong>{g.t}</strong>
            <ul className="bullets" style={{ marginTop: '.6rem' }}>{g.items.map((x) => <li key={x}>{x}</li>)}</ul>
          </div>
        ))}
      </div>
      <div className="alert info" style={{ marginTop: '1.25rem' }}>{t('landing.impact.note')}</div>
    </Section>
  );
}

export function Technology() {
  const { t } = useTranslation();
  return (
    <Section>
      <Heading title={t('landing.tech.title')} />
      <p className="lead-line">{t('landing.tech.lead')}</p>
      <p className="muted" style={{ marginBottom: '.75rem' }}>{t('landing.tech.body')}</p>
      <div className="tag-row">{list(t, 'landing.tech.items').map((x) => <span key={x} className="chip">{x}</span>)}</div>
      <p className="muted" style={{ margin: '1rem 0' }}>{t('landing.tech.after')}</p>
      <Chain items={list(t, 'landing.tech.chain')} />
    </Section>
  );
}

export function Story() {
  const { t } = useTranslation();
  return (
    <Section tone="white">
      <Heading title={t('landing.story.title')} sub={t('landing.story.lead')} />
      <div className="story">
        <ul className="beats">{list(t, 'landing.story.beats').map((x) => <li key={x}>{x}</li>)}</ul>
        <div className="stack">
          <p>{t('landing.story.p1')}</p>
          <p><strong>{t('landing.story.p2')}</strong></p>
          <p>{t('landing.story.p3')}</p>
          <p className="lead-line">{t('landing.story.born')}</p>
        </div>
      </div>
    </Section>
  );
}

export function Vision() {
  const { t } = useTranslation();
  return (
    <Section>
      <Heading title={t('landing.vision.title')} />
      <p className="lead-line">{t('landing.vision.lead')}</p>
      <Checklist items={list(t, 'landing.vision.items')} />
      <p className="muted" style={{ marginTop: '1rem' }}>{t('landing.vision.after')}</p>
    </Section>
  );
}

export function Audiences() {
  const { t } = useTranslation();
  const c = 'landing.audiences.companies';
  const p = 'landing.audiences.pros';
  const pt = 'landing.audiences.partners';
  return (
    <>
      <Section id="entreprises" tone="white">
        <Heading kicker={t(`${c}.kicker`)} title={t(`${c}.title`)} />
        <p className="muted" style={{ marginBottom: '.75rem' }}>{t(`${c}.lead`)}</p>
        <Checklist items={list(t, `${c}.items`)} />
        <div style={{ marginTop: '1.25rem' }}><CtaLink to={requestLink('workforce')}>{t(`${c}.cta`)}</CtaLink></div>
      </Section>
      <Section id="professionnels">
        <Heading kicker={t(`${p}.kicker`)} title={t(`${p}.title`)} />
        <p className="muted" style={{ marginBottom: '1rem' }}>{t(`${p}.lead`)}</p>
        <Chain items={list(t, `${p}.chain`)} />
        <div style={{ marginTop: '1.25rem' }}><CtaLink to="/register">{t(`${p}.cta`)}</CtaLink></div>
      </Section>
      <Section id="partenaires" tone="white">
        <Heading kicker={t(`${pt}.kicker`)} title={t(`${pt}.title`)} />
        <p className="muted" style={{ marginBottom: '.75rem' }}>{t(`${pt}.lead`)}</p>
        <div className="tag-row">{list(t, `${pt}.items`).map((x) => <span key={x} className="chip">{x}</span>)}</div>
        <div style={{ marginTop: '1.25rem' }}><CtaLink to={requestLink('partner')}>{t(`${pt}.cta`)}</CtaLink></div>
      </Section>
    </>
  );
}

export function Investors() {
  const { t } = useTranslation();
  return (
    <Section id="investisseurs">
      <Heading kicker={t('landing.investors.kicker')} title={t('landing.investors.title')} />
      <p className="muted" style={{ marginBottom: '.75rem' }}>{t('landing.investors.lead')}</p>
      <Chain items={list(t, 'landing.investors.chain')} />
      <p className="muted" style={{ margin: '1rem 0 1.5rem' }}>{t('landing.investors.body')}</p>
      <div className="grid cols4">
        {list(t, 'landing.investors.pillars').map((x, i) => (
          <div key={x.t} className="card pad step">
            <span className="step-n">{String(i + 1).padStart(2, '0')}</span>
            <strong>{x.t}</strong>
            <span className="muted">{x.d}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: '1.25rem' }}><CtaLink to={requestLink('investor')}>{t('landing.investors.cta')}</CtaLink></div>
    </Section>
  );
}

export function Proof() {
  const { t } = useTranslation();
  return (
    <Section tone="white">
      <Heading title={t('landing.proof.title')} sub={t('landing.proof.sub')} />
      <div className="alert info">{t('landing.proof.empty')}</div>
    </Section>
  );
}

export function Faq() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(0);
  const items = list(t, 'landing.faq.items');
  return (
    <Section id="faq">
      <Heading title={t('landing.faq.title')} />
      <div className="faq">
        {items.map((x, i) => (
          <div key={x.q} className={`faq-item ${open === i ? 'open' : ''}`}>
            <button type="button" aria-expanded={open === i} onClick={() => setOpen(open === i ? -1 : i)}>
              <span>{x.q}</span><i className={`bi bi-chevron-${open === i ? 'up' : 'down'}`} />
            </button>
            {open === i && <p>{x.a}</p>}
          </div>
        ))}
      </div>
    </Section>
  );
}

export function FinalCta() {
  const { t } = useTranslation();
  const b = 'landing.final.buttons';
  return (
    <section className="section final-cta">
      <div className="container center">
        <h2>{t('landing.final.title')}</h2>
        <p>{t('landing.final.sub')}</p>
        <div className="cta-row">
          <Link to="/technicians" className="btn accent"><i className="bi bi-tools" /> {t(`${b}.find`)}</Link>
          <Link to="/register" className="btn ghost"><i className="bi bi-person-workspace" /> {t(`${b}.join`)}</Link>
          <Link to={requestLink('workforce')} className="btn ghost"><i className="bi bi-building" /> {t(`${b}.talents`)}</Link>
          <Link to={requestLink('partner')} className="btn ghost"><i className="bi bi-people" /> {t(`${b}.partner`)}</Link>
          <Link to={requestLink('investor')} className="btn ghost"><i className="bi bi-briefcase" /> {t(`${b}.invest`)}</Link>
        </div>
      </div>
    </section>
  );
}
